/**
 * BHADKO OTT - Television 10-Foot Spatial Navigation Controller
 * Handles directional navigation (Arrow keys / D-pad), Enter/OK selection,
 * Escape/Back navigation, and high-contrast focus rings for Smart TV and Android TV browsers.
 */

export class TVNavigator {
  constructor(options = {}) {
    this.isEnabled = false;
    this.currentFocusedElement = null;
    this.onModeChange = options.onModeChange || (() => {});
    this.focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), .channel-card, .sidebar-channel-item, .nav-btn, .category-pill';
    
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.init();
  }

  init() {
    window.addEventListener('keydown', this.handleKeyDown);
  }

  enable() {
    this.isEnabled = true;
    document.body.classList.add('tv-mode-active');
    this.focusFirstAvailable();
    this.onModeChange(true);
  }

  disable() {
    this.isEnabled = false;
    document.body.classList.remove('tv-mode-active');
    if (this.currentFocusedElement) {
      this.currentFocusedElement.classList.remove('tv-focused');
    }
    this.onModeChange(false);
  }

  toggle() {
    if (this.isEnabled) {
      this.disable();
    } else {
      this.enable();
    }
    return this.isEnabled;
  }

  getFocusableElements() {
    const all = Array.from(document.querySelectorAll(this.focusableSelector));
    // Filter visible elements
    return all.filter(el => {
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      return (
        rect.width > 0 &&
        rect.height > 0 &&
        style.visibility !== 'hidden' &&
        style.display !== 'none' &&
        !el.disabled &&
        !el.closest('.hidden')
      );
    });
  }

  focusFirstAvailable() {
    const list = this.getFocusableElements();
    if (list.length > 0) {
      this.setFocus(list[0]);
    }
  }

  setFocus(element) {
    if (!element) return;
    
    if (this.currentFocusedElement) {
      this.currentFocusedElement.classList.remove('tv-focused');
    }

    this.currentFocusedElement = element;
    element.classList.add('tv-focused');
    
    // Ensure element is visible with smooth scroll
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });

    element.focus({ preventScroll: true });
  }

  handleKeyDown(e) {
    // Check if TV mode is on or user is using arrow keys to trigger it
    const isArrowKey = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key);
    
    if (!this.isEnabled) {
      // If user uses arrow keys and wants TV mode, we can allow arrow navigation
      if (isArrowKey && document.activeElement === document.body) {
        this.enable();
      } else {
        return;
      }
    }

    // Ignore if typing inside text input
    if (document.activeElement?.tagName === 'INPUT' && !['Escape', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      return;
    }

    if (e.key === 'Escape' || e.key === 'Backspace') {
      if (document.activeElement?.tagName === 'INPUT') {
        document.activeElement.blur();
        e.preventDefault();
        return;
      }
      // If modal is open, let modal close
      const openModal = document.querySelector('.modal-backdrop:not(.hidden)');
      if (openModal) {
        return; // Modal handles its own escape
      }
      // Exit fullscreen if active
      if (document.fullscreenElement) {
        document.exitFullscreen();
        e.preventDefault();
        return;
      }
    }

    if (isArrowKey) {
      e.preventDefault();
      this.navigateDirection(e.key);
    } else if (e.key === 'Enter' || e.key === ' ') {
      if (this.currentFocusedElement) {
        // Trigger click
        this.currentFocusedElement.click();
      }
    }
  }

  navigateDirection(direction) {
    const focusables = this.getFocusableElements();
    if (focusables.length === 0) return;

    if (!this.currentFocusedElement || !focusables.includes(this.currentFocusedElement)) {
      this.setFocus(focusables[0]);
      return;
    }

    const currentRect = this.currentFocusedElement.getBoundingClientRect();
    const currentCenter = {
      x: currentRect.left + currentRect.width / 2,
      y: currentRect.top + currentRect.height / 2
    };

    let bestCandidate = null;
    let minDistance = Infinity;

    for (const target of focusables) {
      if (target === this.currentFocusedElement) continue;

      const targetRect = target.getBoundingClientRect();
      const targetCenter = {
        x: targetRect.left + targetRect.width / 2,
        y: targetRect.top + targetRect.height / 2
      };

      const dx = targetCenter.x - currentCenter.x;
      const dy = targetCenter.y - currentCenter.y;

      let isAllowedDirection = false;
      let primaryDist = 0;
      let secondaryDist = 0;

      switch (direction) {
        case 'ArrowUp':
          isAllowedDirection = dy < -8; // Target must be above
          primaryDist = Math.abs(dy);
          secondaryDist = Math.abs(dx);
          break;
        case 'ArrowDown':
          isAllowedDirection = dy > 8; // Target must be below
          primaryDist = Math.abs(dy);
          secondaryDist = Math.abs(dx);
          break;
        case 'ArrowLeft':
          isAllowedDirection = dx < -8; // Target must be to the left
          primaryDist = Math.abs(dx);
          secondaryDist = Math.abs(dy);
          break;
        case 'ArrowRight':
          isAllowedDirection = dx > 8; // Target must be to the right
          primaryDist = Math.abs(dx);
          secondaryDist = Math.abs(dy);
          break;
      }

      if (isAllowedDirection) {
        // Spatial distance weighting: prioritize aligned elements
        const score = primaryDist + secondaryDist * 1.8;
        if (score < minDistance) {
          minDistance = score;
          bestCandidate = target;
        }
      }
    }

    if (bestCandidate) {
      this.setFocus(bestCandidate);
    }
  }
}
